import React, {useState} from "react";
import {
    Avatar,
    Box,
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Property from "crqlar-react-common/dist/components/Property";
import {countryLanguageOptions} from "crqlar-react-common/dist/components/inputs/countryLanguageOptions";
import {MultiSelect, SingleSelect} from "../../common/newSelect";
import type {SelectOption} from "../../common/newSelect";
import NodeSelectField from "../../marketing/automations/components/v2/fields/NodeSelectField";
import NodeMultiSelectField from "../../marketing/automations/components/v2/fields/NodeMultiSelectField";

const RESPONSE_METRICS: SelectOption[] = [
    {value: "", label: "Any response"},
    {value: "NPS", label: "Recommendation"},
    {value: "SATISFACTION", label: "Satisfaction"},
    {value: "SCORE", label: "Score"},
];

const OPERATORS: SelectOption[] = [
    {value: "equals", label: "Equals"},
    {value: "not_equals", label: "Not equals"},
    {value: "contains", label: "Contains"},
    {value: "not_contains", label: "Does not contain"},
    {value: "starts_with", label: "Starts with"},
    {value: "ends_with", label: "Ends with"},
    {value: "exists", label: "Exists"},
    {value: "not_exists", label: "Does not exist"},
];

const DAYS_OF_MONTH: SelectOption[] = [
    {value: "", label: "Day of the start date (5)"},
    ...Array.from({length: 31}, (_, index) => ({value: String(index + 1), label: String(index + 1)})),
];

const SURVEYS: SelectOption[] = [
    {value: "s1", label: "Feedback nach dem Aufenthalt", group: "Post stay"},
    {value: "s2", label: "Zimmerqualität", group: "Post stay"},
    {value: "s3", label: "Wiederkehrende Gäste", group: "Post stay"},
    {value: "s4", label: "Erwartungen vor der Anreise", group: "Pre arrival"},
    {value: "s5", label: "Anreisezeit und Wünsche", group: "Pre arrival"},
    {value: "s6", label: "Abendessen im Restaurant", group: "Restaurant"},
    {value: "s7", label: "Frühstück", group: "Restaurant"},
    {value: "s8", label: "Weinbegleitung", group: "Restaurant"},
    {value: "s9", label: "Behandlung im Spa", group: "Spa"},
    {value: "s10", label: "Saunabereich", group: "Spa"},
    {value: "s11", label: "Mitarbeiterzufriedenheit", group: "Other"},
    {value: "s12", label: "Veranstaltungen", group: "Other"},
];

const STATUSES: SelectOption[] = [
    {value: "PENDING", label: "Pending"},
    {value: "CONFIRMED", label: "Confirmed"},
    {value: "ACTIVE", label: "Checked in"},
    {value: "COMPLETED", label: "Completed"},
    {value: "CANCELLED", label: "Cancelled"},
    {value: "NO_SHOW", label: "No show"},
];

const WEEKDAYS: SelectOption[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    .map(day => ({value: day.toUpperCase(), label: day}));

const SECTORS: SelectOption[] = [
    {value: "sec1", label: "Room reservations", group: "Accommodation"},
    {value: "sec2", label: "Restaurant", group: "Restaurant"},
    {value: "sec3", label: "Bar & Lounge", group: "Restaurant"},
    {value: "sec4", label: "Spa & Beauty", group: "Spa"},
    {value: "sec5", label: "Weekly Planner", group: "Weekly Planner"},
    {value: "sec6", label: "Sports", group: "Other"},
];

// Two colleagues share a name; the email at the right end tells them apart.
const TEAM: SelectOption[] = [
    ["Anna Huber", "anna.huber@example.com"],
    ["Anna Huber", "a.huber@example.com"],
    ["Bernd Gruber", "bernd.gruber@example.com"],
    ["Clara Wimmer", "clara.wimmer@example.com"],
    ["David Leitner", "david.leitner@example.com"],
    ["Eva Steiner", "eva.steiner@example.com"],
    ["Felix Moser", "felix.moser@example.com"],
    ["Greta Hofer", "greta.hofer@example.com"],
    ["Hannes Berger", "hannes.berger@example.com"],
    ["Ida Fuchs", "ida.fuchs@example.com"],
    ["Jakob Pichler", "jakob.pichler@example.com"],
    ["Klara Eder", "klara.eder@example.com"],
].map(([label, detail], index) => ({value: `u${index}`, label, detail}));

const RULE_FIELDS: SelectOption[] = [
    ...["Score", "Recommendation class", "Satisfaction class", "Guest language", "Guest email", "Survey name", "Submitted at"]
        .map(label => ({value: `{{"Feedback erhalten".${label}}}`, label, group: "Feedback erhalten"})),
    ...["Reservation code", "Sector", "Service", "Arrival", "Departure", "Status"]
        .map(label => ({value: `{{"Reservierung".${label}}}`, label, group: "Reservierung"})),
    {value: "{{\"Team\".count}}", label: "Member count", group: "Umfrage-Team"},
];

const RECOMMENDATION_CLASSES: SelectOption[] = [
    {value: "PROMOTER", label: "Promoter"},
    {value: "PASSIVE", label: "Passive"},
    {value: "DETRACTOR", label: "Detractor"},
];

const RESOURCES: SelectOption[] = [
    {value: "anna", label: "Anna Berger", group: "Therapists"},
    {value: "lisa", label: "Lisa Mayer", group: "Therapists"},
    {value: "markus", label: "Markus Hofer", group: "Therapists"},
    {value: "sophie", label: "Sophie Wallner", group: "Therapists"},
    {value: "jonas", label: "Jonas Ebner", group: "Therapists"},
    {value: "room1", label: "Treatment room 1", group: "Rooms"},
    {value: "room2", label: "Treatment room 2", group: "Rooms"},
    {value: "room3", label: "Treatment room 3", group: "Rooms"},
    {value: "couples", label: "Couples suite", group: "Rooms"},
    {value: "relax", label: "Relax room", group: "Rooms"},
    {value: "sauna", label: "Sauna", group: "Rooms"},
    {value: "pool", label: "Pool", group: "Rooms"},
];

const resourceIcon = (group?: string) => group === "Rooms"
    ? <MeetingRoomOutlinedIcon sx={{fontSize: 18, color: "text.secondary"}}/>
    : <PersonOutlineIcon sx={{fontSize: 18, color: "text.secondary"}}/>;

/** The same resources with a start icon each, and a chip tone by state: green done, red needs attention. */
const RESOURCES_WITH_ICONS: SelectOption[] = RESOURCES.map(resource => ({
    ...resource,
    adornment: resourceIcon(resource.group),
    tone: resource.value === "anna" ? "success" : resource.value === "room2" ? "error" : undefined,
}));

/** The country picker's own flags, from the shared country list. */
const COUNTRIES: SelectOption[] = countryLanguageOptions("country", "en")
    .filter(country => country.group === "Common")
    .map(country => ({
        value: country.code,
        label: country.label,
        adornment: <Avatar src={country.flag} alt="" sx={{width: 18, height: 18}}/>,
    }));

const resourceOf = (value: string) => RESOURCES.find(resource => resource.value === value)!;

/** The booking engine's services: sector › category › service, bookable online or not. */
const BOOKING_SERVICES = [
    {id: "hotstone", name: "Hot stone massage", sector: "Spa", categories: ["Massages"], bookable: true},
    {id: "classic", name: "Classic massage", sector: "Spa", categories: ["Massages"], bookable: true},
    {id: "couples", name: "Couples massage", sector: "Spa", categories: ["Massages", "Packages"], bookable: true},
    {id: "facial", name: "Classic facial", sector: "Spa", categories: ["Facials"], bookable: true},
    {id: "antiaging", name: "Anti-aging facial", sector: "Spa", categories: ["Facials"], bookable: false},
    {id: "spaday", name: "Spa day package", sector: "Spa", categories: ["Packages"], bookable: true},
    {id: "dinner", name: "Five-course dinner", sector: "Restaurant", categories: ["Dinner"], bookable: true},
    {id: "chefs", name: "Chef's table", sector: "Restaurant", categories: ["Dinner"], bookable: false},
    {id: "buffet", name: "Breakfast buffet", sector: "Restaurant", categories: ["Breakfast"], bookable: true},
    {id: "late", name: "Late breakfast", sector: "Restaurant", categories: ["Breakfast"], bookable: true},
    {id: "double", name: "Double room", sector: "Accommodation", categories: ["Rooms"], bookable: true},
    {id: "suite", name: "Junior suite", sector: "Accommodation", categories: ["Rooms"], bookable: false},
];

const serviceOf = (id: string) => BOOKING_SERVICES.find(service => service.id === id);

interface Rule {
    field: string;
    operator: string;
    value: string;
    autoFocus?: boolean;
}

const SectionHeader = ({label}: {label: string}) => (
    <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, mb: 1}}
    >
        {label}
    </Typography>
);

/** The control at the width of a node form's right column, with its current value underneath. */
const Demo = ({value, children}: {value: string | string[]; children: React.ReactNode}) => (
    <Stack gap={0.75} sx={{width: "100%", maxWidth: 420}}>
        {children}
        <Typography variant="body2" color="text.secondary" noWrap>
            value: {Array.isArray(value) ? `[${value.join(", ")}]` : JSON.stringify(value)}
        </Typography>
    </Stack>
);

const RuleRow = ({rule, setRule}: {rule: Rule; setRule: (rule: Rule) => void}) => {
    const enumValue = rule.field.includes("Recommendation class") && rule.operator === "equals";

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {xs: "minmax(0, 1fr) minmax(0, 1fr) 34px", sm: "minmax(0, 1.8fr) minmax(0, 1fr) minmax(0, 1.2fr) 34px"},
                columnGap: 1,
                rowGap: 1,
                alignItems: "center",
            }}
        >
            <Box sx={{minWidth: 0, gridColumn: {xs: "1 / -1", sm: "auto"}}}>
                <SingleSelect
                    placeholder="Field"
                    value={rule.field}
                    options={RULE_FIELDS}
                    onChange={field => setRule({...rule, field, operator: "equals", value: ""})}
                    autoFocus={rule.autoFocus}
                />
            </Box>

            <Box sx={{minWidth: 0}}>
                <SingleSelect
                    placeholder="Operator"
                    value={rule.operator}
                    options={OPERATORS}
                    onChange={operator => setRule({...rule, operator})}
                    disabled={!rule.field}
                />
            </Box>

            <Box sx={{minWidth: 0}}>
                {enumValue ? (
                    <SingleSelect
                        placeholder="Value"
                        value={rule.value}
                        options={RECOMMENDATION_CLASSES}
                        onChange={value => setRule({...rule, value})}
                    />
                ) : (
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Value"
                        value={rule.value}
                        disabled={!rule.field}
                        onChange={event => setRule({...rule, value: event.target.value})}
                        inputProps={{"aria-label": "Value"}}
                    />
                )}
            </Box>

            <IconButton size="small" aria-label="Rule options">
                <MoreVertIcon/>
            </IconButton>
        </Box>
    );
};

const SelectsTestPage = () => {
    const [metric, setMetric] = useState("");
    const [operator, setOperator] = useState("starts_with");
    const [dayOfMonth, setDayOfMonth] = useState("");
    const [survey, setSurvey] = useState("s1");
    const [status, setStatus] = useState("");
    const [statusWithoutClear, setStatusWithoutClear] = useState("CONFIRMED");
    const [missingSurvey, setMissingSurvey] = useState("s-deleted");
    const [listLoading, setListLoading] = useState(false);

    const [weekdays, setWeekdays] = useState<string[]>(["MON", "FRI"]);
    const [weekdaysWithoutClear, setWeekdaysWithoutClear] = useState<string[]>(["MON", "FRI"]);
    const [sectors, setSectors] = useState<string[]>([]);
    const [people, setPeople] = useState<string[]>(["u0"]);
    const [statuses, setStatuses] = useState<string[]>(["CONFIRMED", "ACTIVE"]);
    const [missingSectors, setMissingSectors] = useState<string[]>(["sec2", "sec-deleted"]);

    const [linked, setLinked] = useState<string[]>(["anna", "room1", "lisa", "room2", "markus"]);
    const [countries, setCountries] = useState<string[]>(["at", "de", "ch"]);
    const [lastAction, setLastAction] = useState("");

    // The booking engine's rule stays in the screen: once a service is picked, the other sectors'
    // services are disabled. The select only draws what it is told.
    const [services, setServices] = useState<string[]>(["hotstone", "couples", "antiaging"]);
    const bookingSector = services.length ? serviceOf(services[0])?.sector : undefined;
    const serviceOptions: SelectOption[] = BOOKING_SERVICES.flatMap(service => service.categories.map(category => ({
        value: service.id,
        label: service.name,
        section: service.sector,
        group: category,
        tone: service.bookable ? "success" as const : "error" as const,
        disabled: !!bookingSector && service.sector !== bookingSector,
    })));
    const editResource = (option: SelectOption) => setLastAction(`Edit ${option.label}`);

    const [rules, setRules] = useState<Rule[]>([
        {field: RULE_FIELDS[3].value, operator: "starts_with", value: "DE"},
        {field: RULE_FIELDS[1].value, operator: "equals", value: "PROMOTER"},
    ]);

    const [oldMetric, setOldMetric] = useState("");
    const [oldSectors, setOldSectors] = useState<string[]>([]);

    const setRule = (index: number) => (rule: Rule) => setRules(current => current.map((entry, at) => at === index ? rule : entry));

    // PageShell bounds the tab at 100dvh and clips it, so this page owns its own scroller.
    return (
        <Box sx={{flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto"}}>
            <Box sx={{p: {xs: 2, sm: 3}, maxWidth: 920, mx: "auto"}}>
                <Typography variant="h5" sx={{fontWeight: 700}}>
                    Selects
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    SingleSelect and MultiSelect from common/newSelect — one Autocomplete base, one row layout,
                    no open animation. Typing searches every list: labels, details and headings.
                    {' '}Row hover clears over group headings; arrow keys keep their active row visible for Enter.
                </Typography>

                {/* ---------------- Single ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="SingleSelect"/>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title="Short list" subtitle="4 options">
                            <Demo value={metric}>
                                <SingleSelect label="Only responses where" value={metric} options={RESPONSE_METRICS} onChange={setMetric}/>
                            </Demo>
                        </Property>

                        <Property title="Eight options" subtitle="The If operator list">
                            <Demo value={operator}>
                                <SingleSelect label="Operator" value={operator} options={OPERATORS} onChange={setOperator}/>
                            </Demo>
                        </Property>

                        <Property title="Long list" subtitle="32 options">
                            <Demo value={dayOfMonth}>
                                <SingleSelect label="Day of month" value={dayOfMonth} options={DAYS_OF_MONTH} onChange={setDayOfMonth}/>
                            </Demo>
                        </Property>

                        <Property title="Grouped" subtitle="Surveys by type · 12 options">
                            <Demo value={survey}>
                                <SingleSelect label="Survey" value={survey} options={SURVEYS} onChange={setSurvey}/>
                            </Demo>
                        </Property>

                        <Property title="Clear selection" subtitle="Pick a status, then clear back to the placeholder">
                            <Demo value={status}>
                                <SingleSelect label="Status" placeholder="Pick a status" value={status} options={STATUSES} onChange={setStatus}/>
                            </Demo>
                        </Property>

                        <Property title="Clear disabled" subtitle="Choose another status to change the selection">
                            <Demo value={statusWithoutClear}>
                                <SingleSelect
                                    label="Status without clear"
                                    value={statusWithoutClear}
                                    options={STATUSES}
                                    onChange={setStatusWithoutClear}
                                    disableClearable
                                />
                            </Demo>
                        </Property>

                        <Property title="Missing value" subtitle="Stored id the list does not offer">
                            <Stack gap={1} sx={{width: "100%", maxWidth: 420}}>
                                <Demo value={missingSurvey}>
                                    <SingleSelect
                                        label="Survey"
                                        value={missingSurvey}
                                        options={listLoading ? [] : SURVEYS}
                                        unknownLabel="Sommer-Umfrage 2025"
                                        loading={listLoading}
                                        onChange={setMissingSurvey}
                                    />
                                </Demo>
                                <FormControlLabel
                                    control={<Switch size="small" checked={listLoading} onChange={event => setListLoading(event.target.checked)}/>}
                                    label="List still loading"
                                    slotProps={{typography: {variant: "body2"}}}
                                />
                            </Stack>
                        </Property>

                        <Property title="Disabled">
                            <Demo value={survey}>
                                <SingleSelect label="Survey" value={survey} options={SURVEYS} onChange={setSurvey} disabled/>
                            </Demo>
                        </Property>
                    </Stack>
                </Paper>

                {/* ---------------- Multi ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="MultiSelect"/>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title="Short list" subtitle="Weekdays · chip removal and clear-all">
                            <Demo value={weekdays}>
                                <MultiSelect label="On these days" placeholder="Pick at least one weekday" value={weekdays} options={WEEKDAYS} onChange={setWeekdays}/>
                            </Demo>
                        </Property>

                        <Property title="Clear-all disabled" subtitle="Individual chip removal stays available">
                            <Demo value={weekdaysWithoutClear}>
                                <MultiSelect
                                    label="Days without clear-all"
                                    value={weekdaysWithoutClear}
                                    options={WEEKDAYS}
                                    onChange={setWeekdaysWithoutClear}
                                    disableClearable
                                />
                            </Demo>
                        </Property>

                        <Property title="Grouped" subtitle="Sectors by type">
                            <Demo value={sectors}>
                                <MultiSelect label="Only in sector" placeholder="Any sector" value={sectors} options={SECTORS} onChange={setSectors}/>
                            </Demo>
                        </Property>

                        <Property title="Bulk select" subtitle="Same sectors · heading checkboxes · indented rows">
                            <Demo value={sectors}>
                                <MultiSelect
                                    label="Bulk sectors"
                                    placeholder="Any sector"
                                    value={sectors}
                                    options={SECTORS}
                                    onChange={setSectors}
                                    bulkSelect
                                />
                            </Demo>
                        </Property>

                        <Property title="Long list" subtitle="Team members · 12 options · email as detail">
                            <Demo value={people}>
                                <MultiSelect label="Only these people" placeholder="Anyone in the team" value={people} options={TEAM} onChange={setPeople}/>
                            </Demo>
                        </Property>

                        <Property title="Missing value" subtitle="Uses the loading switch above">
                            <Demo value={missingSectors}>
                                <MultiSelect
                                    label="Only in sector"
                                    placeholder="Any sector"
                                    value={missingSectors}
                                    options={listLoading ? [] : SECTORS}
                                    unknownLabels={{"sec2": "Restaurant", "sec-deleted": "Pool Bar"}}
                                    loading={listLoading}
                                    onChange={setMissingSectors}
                                />
                            </Demo>
                        </Property>

                        <Property title="Disabled">
                            <Demo value={statuses}>
                                <MultiSelect label="Only these statuses" value={statuses} options={STATUSES} onChange={setStatuses} disabled/>
                            </Demo>
                        </Property>
                    </Stack>
                </Paper>

                {/* ---------------- Picked values ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="Picked values"/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, mt: -0.5}}>
                        Remove individual chips with their close buttons, untick options in the list, or clear the whole field.
                        Edit buttons act separately; Backspace in an empty search keeps the selection.
                        {lastAction ? ` Last action: ${lastAction}.` : ""}
                    </Typography>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title="Chips with edit" subtitle="Neutral grey · edit and remove · 13px text · 6px corners">
                            <Demo value={linked}>
                                <MultiSelect
                                    label="Linked resources"
                                    placeholder="Add a resource"
                                    value={linked}
                                    options={RESOURCES}
                                    onChange={setLinked}
                                    onEdit={value => editResource(resourceOf(value))}
                                />
                            </Demo>
                        </Property>

                        <Property title="Start icon + tone" subtitle="Same values · Anna green, Treatment room 2 red">
                            <Demo value={linked}>
                                <MultiSelect
                                    label="Linked resources"
                                    placeholder="Add a resource"
                                    value={linked}
                                    options={RESOURCES_WITH_ICONS}
                                    onChange={setLinked}
                                    onEdit={value => editResource(resourceOf(value))}
                                />
                            </Demo>
                        </Property>

                        <Property title="Flags" subtitle="The country picker's flags">
                            <Demo value={countries}>
                                <MultiSelect
                                    label="Guest countries"
                                    placeholder="Any country"
                                    value={countries}
                                    options={COUNTRIES}
                                    onChange={setCountries}
                                    onEdit={value => setLastAction(`Edit ${COUNTRIES.find(country => country.value === value)?.label}`)}
                                />
                            </Demo>
                        </Property>

                        <Property title="Disabled chip actions" subtitle="Selection, edit and removal are disabled">
                            <Demo value={linked}>
                                <MultiSelect
                                    label="Disabled linked resources"
                                    value={linked}
                                    options={RESOURCES}
                                    onChange={setLinked}
                                    onEdit={value => editResource(resourceOf(value))}
                                    disabled
                                />
                            </Demo>
                        </Property>
                    </Stack>
                </Paper>

                {/* ---------------- Booking engine ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="Booking engine — services"/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, mt: -0.5}}>
                        Sector › category › service. With bulkSelect every heading ticks everything under it. Couples massage sits under Massages and
                        Packages and is one pick. Once a service is picked, the other sectors are disabled. Green: bookable online,
                        red: not.
                        {lastAction ? ` Last action: ${lastAction}.` : ""}
                    </Typography>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title="Services" subtitle="13 rows · search by service, category or sector">
                            <Demo value={services}>
                                <MultiSelect
                                    label="Services"
                                    placeholder="Pick services"
                                    value={services}
                                    options={serviceOptions}
                                    onChange={setServices}
                                    bulkSelect
                                    onEdit={value => setLastAction(`Edit ${serviceOf(value)?.name}`)}
                                />
                            </Demo>
                        </Property>
                    </Stack>
                </Paper>

                {/* ---------------- Rule row ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="Without a label — If rule row"/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, mt: -0.5}}>
                        Field (14 options) › operator › value, with a plain text field beside the selects to check the heights line up.
                        A new rule focuses its field and opens the list.
                    </Typography>

                    <Paper variant="outlined" sx={{p: 1.5}}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                            WHERE
                        </Typography>
                        <Stack gap={1}>
                            {rules.map((rule, index) => (
                                <RuleRow key={index} rule={rule} setRule={setRule(index)}/>
                            ))}
                        </Stack>
                        <Stack direction="row" sx={{mt: 1}}>
                            <Button size="small" startIcon={<AddIcon/>} onClick={() => setRules(current => [...current, {field: "", operator: "", value: "", autoFocus: true}])}>
                                Add rule
                            </Button>
                        </Stack>
                    </Paper>
                </Paper>

                {/* ---------------- Before ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, borderRadius: 2}}>
                    <SectionHeader label="Before — current node fields"/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, mt: -0.5}}>
                        NodeSelectField (MUI Select, animated menu) and NodeMultiSelectField as the node forms use them today, for comparison.
                    </Typography>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title="NodeSelectField" subtitle="Short list">
                            <Demo value={oldMetric}>
                                <NodeSelectField label="Only responses where" value={oldMetric} options={RESPONSE_METRICS} onChange={setOldMetric}/>
                            </Demo>
                        </Property>

                        <Property title="NodeMultiSelectField" subtitle="Sectors by type">
                            <Demo value={oldSectors}>
                                <NodeMultiSelectField label="Only in sector" placeholder="Any sector" value={oldSectors} options={SECTORS} onChange={setOldSectors}/>
                            </Demo>
                        </Property>
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );
};

export default SelectsTestPage;
